import React, { useEffect, useState, useCallback } from "react";
import "./OptionChainAnalysis.css";
import { environment } from "environment/environment";
import useNiftyWebsocket from "service/websocket/NiftyWebsocketApi";
import usePageTitle from "component/common/PageTitle";

export default function OptionChainAnalysis() {
  // WebSocket connection
  const { options } = useNiftyWebsocket(environment.niftyWebsocketUrl);
  const { nseWebsiteOIResponseData } = useNiftyWebsocket(environment.niftyWebsocketUrl);
  const [indexDayRangeData, setIndexDayRangeData] = useState(null);
  const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
  const [allStrikeNiftyOptionDataList, setAllStrikeNiftyOptionDataModelList] = useState(null);
  const [indexFilterValueChanges, setIndexFilterValueChangeFilter] = useState("NSE:NIFTY");
  const [marketSignal, setMarketSignal] = useState("NO_SIGNAL");
  const [blinkSignal, setBlinkSignal] = useState(true);
  const [filter5thStrikeNiftyOptionDataList, setFilter5thStrikeNiftyOptionDataModelList] = useState(null);
  const [calculate5thStrikeNCEPEVAlues, setCalculate5thStrikeNCEPEVAlues] = useState(null);
  const [nseWebsiteOIResposneDataList, setNseWebsiteOIResposneDataList] = useState(null);
  const [preopenMarketData, setPreopenMarketData] = useState(null);
  const [liveAnalysisMarketData, setLiveAnalysisMarketData] = useState(null);
  // =======================
  // Stats State
  // =======================
  const [stats, setStats] = useState({
    spotPrice: null,
    highestCallVolume: null,
    secondHighestCallVolume: null,
    highestPutVolume: null,
    secondHighestPutVolume: null,
    highestCallOi: null,
    secondHighestCallOi: null,
    highestPutOi: null,
    secondHighestPutOi: null,
    highestCallOiChg: null,
    secondHighestCallOiChg: null,
    highestPutOiChg: null,
    secondHighestPutOiChg: null,
    highestCall3MOIChange: null,
    secondHighestCall3MOIChange: null,
    highestPut3MOIChange: null,
    secondHighestPut3MOIChange: null,
    highestCallTikVolumeChange: null,
    secondHighestCallTikVolumeChange: null,
    highestPutTikVolumeChange: null,
    secondHighestPutTikVolumeChange: null,
    highestCall3MVolumeChange: null,
    secondHighestCall3MVolumeChange: null,
    highestPut3MVolumeChange: null,
    secondHighestPut3MVolumeChange: null,
    highestCall3MOICHGChange: null,
    secondHighestCall3MOICHGChange: null,
    highestPut3MOICHGChange: null,
    secondHighestPut3MOICHGChange: null,

    highestCallVolStrike: null,
    secondHighestCallVolStrike: null,
    highestCallOiStrike: null,
    secondHighestCallOiStrike: null,
    highestCallOiChgStrike: null,
    secondHighestCallOiChgStrike: null,
    highestPutVolStrike: null,
    secondHighestPutVolStrike: null,
    highestPutOiStrike: null,
    secondHighestPutOiStrike: null,
    highestPutOiChgStrike: null,
    secondHighestPutOiChgStrike: null,

    callResistance: null,
    callResistanceWttAndWtb: null,
    callResistanceOIWttAndWtb: null,
    callResistanceOIChgWttAndWtb: null,
    callResistanceOIChgWttAndWtb: null,

    putSupport: null,
    putSupportWttAndWtb: null,
    putSupportOIWttAndWtb: null,
    putSupportOIChgWttAndWtb: null,

    callTotalVolumeSum: null,
    callTotalOISum: null,
    callTotalOICHGSum: null,

    putTotalVolumeSum: null,
    putTotalOISum: null,
    putTotalOICHGSum: null,

    callHighestTotalVolume: null,
    callSecondHighestHighestTotalVolume: null,
  });

  const [statsAllStrikeData, setAllStrikeData] = useState({
    callAllStrikeTotalVolumeSum: null,
    callAllStrikeTotalOISum: null,
    callAllStrikeTotalOICHGSum: null,

    putAllStrikeTotalVolumeSum: null,
    putAllStrikeTotalOISum: null,
    putAllStrikeTotalOICHGSum: null,

    highestCallTotalVolume: null,
    secondHighestCallTotalVolume: null,
    highestPutTotalVolume: null,
    secondHighestPutTotalVolume: null,

    highestCallTotalVolumeStrike: null,
    secondHighestCallTotalVolumeStrike: null,
    highestPutTotalVolumeStrike: null,
    secondHighestPutTotalVolumeStrike: null,

    highestCallAllOI: null,
    secondHighestCallAllOI: null,
    highestCallAllOIChg: null,
    secondHighestCallAllOIChg: null,

    highestPutAllOI: null,
    secondHighestPutAllOI: null,
    highestPutAllOIChg: null,
    secondHighestPutAllOIChg: null,

    highestCallAllStrikeOI: null,
    secondHighestCallStrikeOI: null,
    highestCallAllStrikeOIChg: null,
    secondHighestCallAllStrikeOIChg: null,

    highestPutAllStrikeOI: null,
    secondHighestPutAllStrikeOI: null,
    highestPutAllStrikeOIChg: null,
    secondHighestPutAllStrikeOIChg: null,



  });
  // Percentage Helpers
  // =======================
  const getPercentage = (value, highest) => highest ? ((value / highest) * 100).toFixed(2) : "0";

  // =======================

  // Resistance Logic
  // =======================
  const getCallResistanceLogic = useCallback((highestCallVolStrike, highestCallOiStrike, spotPrice) => {
    if (highestCallVolStrike == null || highestCallOiStrike == null) return null;

    if (!highestCallVolStrike || !highestCallOiStrike) return null;

    if (highestCallVolStrike === highestCallOiStrike) return highestCallVolStrike;

    const volDiff = Math.abs(highestCallVolStrike - spotPrice);
    const oiDiff = Math.abs(highestCallOiStrike - spotPrice);
    return volDiff < oiDiff ? highestCallVolStrike : highestCallOiStrike;
  }, []);

  const getCallResistanceWttAndWtb = useCallback((callResistance, secondHighestCallVolStrike, secondHighestCallVolume, highestCallVolume) => {
    if (!callResistance || !secondHighestCallVolStrike || !secondHighestCallVolume) return "N/A";

    const percentage = getPercentage(secondHighestCallVolume, highestCallVolume);
    const per = parseFloat(percentage);

    if (secondHighestCallVolStrike < callResistance) return per >= 75 ? `WTB - ${percentage}%` : "STRNG";
    return per >= 75 ? `WTT - ${percentage}%` : "STRNG";
  }, []);

  // React-compatible version
  const getCallResistanceOIWttAndWtb = useCallback((secondHighestCallOIStrike, secondHighestCallOI, highestCallOIStrike) => {
    let callResistanceOIWttAndWtb = "";
    if (highestCallOIStrike != null && secondHighestCallOIStrike != null && secondHighestCallOIStrike < highestCallOIStrike) {
      const percentage = getCallOiPercentage(secondHighestCallOI);
      const per = parseInt(percentage);
      if (per >= 75) {
        callResistanceOIWttAndWtb = `WTB - ${percentage}`;
        // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
      } else {
        callResistanceOIWttAndWtb = `STRNG`;
        // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
      }
    } else {
      const percentage = getCallOiPercentage(secondHighestCallOI);
      const per = parseInt(percentage);
      if (per >= 75) {
        callResistanceOIWttAndWtb = `WTT - ${percentage}`;
      } else {
        callResistanceOIWttAndWtb = `STRNG`;
      }
      // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
    }

    return callResistanceOIWttAndWtb;
  }, []);

  // React-compatible version
  const getCallResistanceOIChgWttAndWtb = useCallback((secondHighestCallOIChgStrike, secondHighestCallOIChg, highestOIChgStrike) => {
    let callResistanceOIChgWttAndWtb = "";
    if (highestOIChgStrike != null && secondHighestCallOIChgStrike != null && secondHighestCallOIChgStrike < highestOIChgStrike) {
      const percentage = getCallOiChgPercentage(secondHighestCallOIChg);
      const per = parseInt(percentage);
      if (per >= 75) {
        callResistanceOIChgWttAndWtb = `WTB - ${percentage}`;
        // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
      } else {
        callResistanceOIChgWttAndWtb = `STRNG`;
        // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
      }
    } else {
      const percentage = getCallOiChgPercentage(secondHighestCallOIChg);
      const per = parseInt(percentage);
      if (per >= 75) {
        callResistanceOIChgWttAndWtb = `WTT - ${percentage}`;
      } else {
        callResistanceOIChgWttAndWtb = `STRNG`;
      }
      // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
    }

    return callResistanceOIChgWttAndWtb;
  }, []);

  // React-compatible version
  const getPutSupportLogic = useCallback((highestPutVolStrike, highestPutOiStrike, spotPrice) => {
    let putSupport = null;
    // 🧱 Support Logic
    if (highestPutVolStrike != null && highestPutOiStrike != null) {
      if (highestPutVolStrike === highestPutOiStrike) {
        putSupport = highestPutVolStrike;
      } else {
        const volDiff = Math.abs(highestPutVolStrike - spotPrice);
        const oiDiff = Math.abs(highestPutOiStrike - spotPrice);
        putSupport = volDiff < oiDiff ? highestPutVolStrike : highestPutOiStrike;
      }
      // console.log("putSupport =>", putSupport);
    }

    return putSupport;
  }, []);

  // React-compatible version
  const getPutSupportWttAndWtb = useCallback((secondHighestPutVolStrike, secondHighestPutVolume, putSupport) => {
    let putSupportWttAndWtb = "";
    if (putSupport != null && secondHighestPutVolStrike != null && secondHighestPutVolStrike < putSupport) {
      const percentage = getPutVolumePercentage(secondHighestPutVolume);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportWttAndWtb = `WTB - ${percentage}`;
        //  console.log("Support is =>", putSupportWttAndWtb);
      } else {
        putSupportWttAndWtb = `STRNG`;
        //   console.log("Support is =>", putSupportWttAndWtb);
      }
    } else {
      const percentage = getPutVolumePercentage(secondHighestPutVolume);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportWttAndWtb = `WTT - ${percentage}`;
      } else {
        putSupportWttAndWtb = `STRNG`;
      }
      //console.log("Support is =>", putSupportWttAndWtb);
    }
    return putSupportWttAndWtb;
  }, []);

  // ✅ React-compatible version
  const getPutSupportOIWttAndWtb = useCallback((secondHighestPutOIStrike, secondHighestPutOI, highestPutOIStrike) => {
    let putSupportOIWttAndWtb = "";
    if (highestPutOIStrike != null && secondHighestPutOIStrike != null && secondHighestPutOIStrike < highestPutOIStrike) {
      const percentage = getPutOiPercentage(secondHighestPutOI);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportOIWttAndWtb = `WTB - ${percentage}`;
        // console.log("Support OI is =>", putSupportOIWttAndWtb);
      } else {
        putSupportOIWttAndWtb = `STRNG`;
        // console.log("Support OI is =>", putSupportOIWttAndWtb);
      }
    } else {
      const percentage = getPutOiPercentage(secondHighestPutOI);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportOIWttAndWtb = `WTT - ${percentage}`;
      } else {
        putSupportOIWttAndWtb = `STRNG`;
      }
      // console.log("Support OI is =>", putSupportOIWttAndWtb);
    }
    return putSupportOIWttAndWtb;
  }, []);

  // ✅ React-compatible version
  const getPutSupportOIChgWttAndWtb = useCallback((secondHighestPutOIChgStrike, secondHighestPutOIChg, highestPutOIChgStrike) => {
    let putSupportOIChgWttAndWtb = "";
    if (highestPutOIChgStrike && secondHighestPutOIChgStrike != null && secondHighestPutOIChgStrike < highestPutOIChgStrike) {
      const percentage = getPutOiChgPercentage(secondHighestPutOIChg);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportOIChgWttAndWtb = `WTB - ${percentage}`;
        // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
      } else {
        putSupportOIChgWttAndWtb = `STRNG`;
        // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
      }
    } else {
      const percentage = getPutOiChgPercentage(secondHighestPutOIChg);
      const per = parseInt(percentage);
      if (per >= 75) {
        putSupportOIChgWttAndWtb = `WTT - ${percentage}`;
      } else {
        putSupportOIChgWttAndWtb = `STRNG`;
      }
      // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
    }
    return putSupportOIChgWttAndWtb;
  }, []);

  // Main Update Function
  // =======================
  const updateTopCallVolumes = useCallback(
    (optionData = []) => {
      if (!Array.isArray(optionData) || optionData.length === 0) return;

      const sortDesc = (arr) => [...new Set(arr)].sort((a, b) => b - a);
      const extract = (key) => optionData.map((row) => row[key]).filter((v) => v != null);
      const findStrike = (key, value) => optionData.find((row) => row[key] === value)?.strikePrice ?? null;

      const spotPrice = optionData[0]?.spotPrice || 0;

      const sortedCallVol = sortDesc(extract("ceVolume"));
      const sortedPutVol = sortDesc(extract("peVolume"));
      const sortedCallOi = sortDesc(extract("ceOI"));
      const sortedPutOi = sortDesc(extract("peOI"));
      const sortedCallOiChg = sortDesc(extract("ceOIChg"));
      const sortedPutOiChg = sortDesc(extract("peOIChg"));
      const sortedCall3MOIChange = sortDesc(extract("ce3MOIChange"));
      const sortedPut3MOIChange = sortDesc(extract("pe3MOIChange"));
      const sortedCallTikVolChange = sortDesc(extract("ceTikByTikVolumeChange"));
      const sortedPutTikVolChange = sortDesc(extract("peTikByTikVolumeChange"));
      const sortedCall3MVolumeChange = sortDesc(extract("ce3MVolumeChange"));
      const sortedPut3MVolumeChange = sortDesc(extract("pe3MVolumeChange"));
      const sortedCall3MOIChgChange = sortDesc(extract("ce3MOICHGChange"));
      const sortedPut3MOIChgChange = sortDesc(extract("pe3MOICHGChange"));

      const updatedStats = {
        spotPrice,
        highestCallVolume: sortedCallVol[0] ?? null,
        secondHighestCallVolume: sortedCallVol[1] ?? null,
        highestPutVolume: sortedPutVol[0] ?? null,
        secondHighestPutVolume: sortedPutVol[1] ?? null,
        highestCallOi: sortedCallOi[0] ?? null,
        secondHighestCallOi: sortedCallOi[1] ?? null,
        highestPutOi: sortedPutOi[0] ?? null,
        secondHighestPutOi: sortedPutOi[1] ?? null,
        highestCallOiChg: sortedCallOiChg[0] ?? null,
        secondHighestCallOiChg: sortedCallOiChg[1] ?? null,
        highestPutOiChg: sortedPutOiChg[0] ?? null,
        secondHighestPutOiChg: sortedPutOiChg[1] ?? null,
        highestCall3MOIChange: sortedCall3MOIChange[0] ?? null,
        secondHighestCall3MOIChange: sortedCall3MOIChange[1] ?? null,
        highestPut3MOIChange: sortedPut3MOIChange[0] ?? null,
        secondHighestPut3MOIChange: sortedPut3MOIChange[1] ?? null,
        highestCallTikVolumeChange: sortedCallTikVolChange[0] ?? null,
        secondHighestCallTikVolumeChange: sortedCallTikVolChange[1] ?? null,
        highestPutTikVolumeChange: sortedPutTikVolChange[0] ?? null,
        secondHighestPutTikVolumeChange: sortedPutTikVolChange[1] ?? null,
        highestCall3MVolumeChange: sortedCall3MVolumeChange[0] ?? null,
        secondHighestCall3MVolumeChange: sortedCall3MVolumeChange[1] ?? null,
        highestPut3MVolumeChange: sortedPut3MVolumeChange[0] ?? null,
        secondHighestPut3MVolumeChange: sortedPut3MVolumeChange[1] ?? null,
        highestCall3MOICHGChange: sortedCall3MOIChgChange[0] ?? null,
        secondHighestCall3MOICHGChange: sortedCall3MOIChgChange[1] ?? null,
        highestPut3MOICHGChange: sortedPut3MOIChgChange[0] ?? null,
        secondHighestPut3MOICHGChange: sortedPut3MOIChgChange[1] ?? null,

        highestCallVolStrike: findStrike("ceVolume", sortedCallVol[0]),
        secondHighestCallVolStrike: findStrike("ceVolume", sortedCallVol[1]),
        highestCallOiStrike: findStrike("ceOI", sortedCallOi[0]),
        secondHighestCallOiStrike: findStrike("ceOI", sortedCallOi[1]),
        highestCallOiChgStrike: findStrike("ceOIChg", sortedCallOiChg[0]),
        secondHighestCallOiChgStrike: findStrike("ceOIChg", sortedCallOiChg[1]),

        highestPutVolStrike: findStrike("peVolume", sortedPutVol[0]),
        secondHighestPutVolStrike: findStrike("peVolume", sortedPutVol[1]),
        highestPutOiStrike: findStrike("peOI", sortedPutOi[0]),
        secondHighestPutOiStrike: findStrike("peOI", sortedPutOi[1]),
        highestPutOiChgStrike: findStrike("peOIChg", sortedPutOiChg[0]),
        secondHighestPutOiChgStrike: findStrike("peOIChg", sortedPutOiChg[1]),

        callTotalVolumeSum: sortedCallVol.reduce((sum, val) => sum + val, 0),
        callTotalOISum: sortedCallOi.reduce((sum, val) => sum + val, 0),
        callTotalOICHGSum: sortedCallOiChg.reduce((sum, val) => sum + val, 0),

        putTotalVolumeSum: sortedPutVol.reduce((sum, val) => sum + val, 0),
        putTotalOISum: sortedPutOi.reduce((sum, val) => sum + val, 0),
        putTotalOICHGSum: sortedPutOiChg.reduce((sum, val) => sum + val, 0),

      };

      // Derived logic
      updatedStats.callResistance = getCallResistanceLogic(updatedStats.highestCallVolStrike, updatedStats.highestCallOiStrike, spotPrice);
      updatedStats.callResistanceWttAndWtb = getCallResistanceWttAndWtb(updatedStats.callResistance, updatedStats.secondHighestCallVolStrike, updatedStats.secondHighestCallVolume, updatedStats.highestCallVolume);
      updatedStats.callResistanceOIWttAndWtb = getCallResistanceOIWttAndWtb(updatedStats.secondHighestCallOiStrike, updatedStats.secondHighestCallOi, updatedStats.highestCallOiStrike);
      updatedStats.callResistanceOIChgWttAndWtb = getCallResistanceOIChgWttAndWtb(updatedStats.secondHighestCallOiChgStrike, updatedStats.secondHighestCallOiChg, updatedStats.highestCallOiChgStrike);

      updatedStats.putSupport = getPutSupportLogic(updatedStats.highestPutVolStrike, updatedStats.highestPutOiStrike, spotPrice);
      updatedStats.putSupportWttAndWtb = getPutSupportWttAndWtb(updatedStats.secondHighestPutVolStrike, updatedStats.secondHighestPutVolume, updatedStats.putSupport);
      updatedStats.putSupportOIWttAndWtb = getPutSupportOIWttAndWtb(updatedStats.secondHighestPutOiStrike, updatedStats.secondHighestPutOi, updatedStats.highestPutOiStrike);
      updatedStats.putSupportOIChgWttAndWtb = getPutSupportOIChgWttAndWtb(updatedStats.secondHighestPutOiChgStrike, updatedStats.secondHighestPutOiChg, updatedStats.highestPutOiChgStrike);

      setStats(updatedStats);
    },
    [getCallResistanceLogic, getCallResistanceWttAndWtb, getCallResistanceOIWttAndWtb, getCallResistanceOIChgWttAndWtb, getPutSupportLogic, getPutSupportWttAndWtb, getPutSupportOIWttAndWtb, getPutSupportOIChgWttAndWtb]
  );

  const updateAllStrikeTotalData = useCallback(
    (optionData = []) => {
      if (!Array.isArray(optionData) || optionData.length === 0) return;

      const sortDesc = (arr) => [...new Set(arr)].sort((a, b) => b - a);
      const extract = (key) => optionData.map((row) => row[key]).filter((v) => v != null);
      const findStrike = (key, value) => optionData.find((row) => row[key] === value)?.strikePrice ?? null;

      const spotPrice = optionData[0]?.spotPrice || 0;

      const sortedCallVol = sortDesc(extract("ceVolume"));
      const sortedPutVol = sortDesc(extract("peVolume"));
      const sortedCallOi = sortDesc(extract("ceOI"));
      const sortedPutOi = sortDesc(extract("peOI"));
      const sortedCallOiChg = sortDesc(extract("ceOIChg"));
      const sortedPutOiChg = sortDesc(extract("peOIChg"));

      const sortedCallTotalVolume = sortDesc(extract("ceTotalVolume"));
      const sortedPutTotalVolume = sortDesc(extract("peTotalVolume"));

      const updatedStats = {
        callAllStrikeTotalVolumeSum: sortedCallVol.reduce((sum, val) => sum + val, 0),
        callAllStrikeTotalOISum: sortedCallOi.reduce((sum, val) => sum + val, 0),
        callAllStrikeTotalOICHGSum: sortedCallOiChg.reduce((sum, val) => sum + val, 0),

        putAllStrikeTotalVolumeSum: sortedPutVol.reduce((sum, val) => sum + val, 0),
        putAllStrikeTotalOISum: sortedPutOi.reduce((sum, val) => sum + val, 0),
        putAllStrikeTotalOICHGSum: sortedPutOiChg.reduce((sum, val) => sum + val, 0),

        highestCallTotalVolume: sortedCallTotalVolume[0] ?? null,
        secondHighestCallTotalVolume: sortedCallTotalVolume[1] ?? null,
        highestPutTotalVolume: sortedPutTotalVolume[0] ?? null,
        secondHighestPutTotalVolume: sortedPutTotalVolume[1] ?? null,

        highestCallAllOI: sortedCallOi[0] ?? null,
        secondHighestCallAllOI: sortedCallOi[1] ?? null,
        highestCallAllOIChg: sortedCallOiChg[0] ?? null,
        secondHighestCallAllOIChg: sortedCallOiChg[1] ?? null,

        highestPutAllOI: sortedPutOi[0] ?? null,
        secondHighestPutAllOI: sortedPutOi[1] ?? null,
        highestPutAllOIChg: sortedPutOiChg[0] ?? null,
        secondHighestPutAllOIChg: sortedPutOiChg[1] ?? null,

        highestCallTotalVolumeStrike: findStrike("ceTotalVolume", sortedCallTotalVolume[0]),
        secondHighestCallTotalVolumeStrike: findStrike("ceTotalVolume", sortedCallTotalVolume[1]),

        highestPutTotalVolumeStrike: findStrike("ceTotalVolume", sortedPutTotalVolume[0]),
        secondHighestPutTotalVolumeStrike: findStrike("ceTotalVolume", sortedPutTotalVolume[1]),


        highestCallAllStrikeOI: findStrike("ceOI", sortedCallOi[0]),
        secondHighestCallAllStrikeOI: findStrike("ceOI", sortedCallOi[1]),

        highestPutAllStrikeOI: findStrike("peOI", sortedPutOi[0]),
        secondHighestPutAllStrikeOI: findStrike("peOI", sortedPutOi[1]),

        highestCallAllStrikeOIChg: findStrike("ceOIChg", sortedCallOiChg[0]),
        secondHighestCallAllStrikeOIChg: findStrike("ceOIChg", sortedCallOiChg[1]),

        highestPutAllStrikeOIChg: findStrike("peOIChg", sortedPutOiChg[0]),
        secondHighestPutStrikeOIChg: findStrike("peOIChg", sortedPutOiChg[1]),




      };
      setAllStrikeData(updatedStats);
    }, []);

  // -------------------
  // Fetch WebSocket Data
  // -------------------
  useEffect(() => {
    const niftyData = options?.[indexFilterValueChanges];

    //if (!niftyData) return;

    if (niftyData?.indexDayRange) setIndexDayRangeData(niftyData.indexDayRange);

    if (Array.isArray(niftyData?.optionDataModelList)) {
      const allStrikeData = niftyData?.optionDataModelList;
      // stats.spotPrice(niftyOptionDataList[0]?.spotPrice || 0);
      const spotPrice = allStrikeData[0]?.spotPrice || 0;
      const filtered10thStrikeData = filterNiftyOptionData(allStrikeData, spotPrice, selectedStrikeFilter);
      setFilter5thStrikeNiftyOptionDataModelList(filterNiftyOptionData(allStrikeData, spotPrice, "5"));
      setCalculate5thStrikeNCEPEVAlues(calculateCEPEValues(filter5thStrikeNiftyOptionDataList, spotPrice));
      updateTopCallVolumes(filtered10thStrikeData); // merged top volumes & OI logic
      console.log('calculate5thStrikeNCEPEVAlues => ', calculate5thStrikeNCEPEVAlues);
      setAllStrikeNiftyOptionDataModelList(allStrikeData);
      // setCeOptionDataList(filterByCallOiChgPercentage(allStrikeData));
      // setPeOptionDataList(filterByPutOiChgPercentage(allStrikeData));
      // setCeOptionDataList(allStrikeData);
      //       setPeOptionDataList(allStrikeData);
      console.log('niftyOptionDataModelList => ', filtered10thStrikeData);
      console.log('Top Call/Put stats => ', stats);
      updateAllStrikeTotalData(allStrikeNiftyOptionDataList);
      console.log('statsAllStrikeData => ', statsAllStrikeData);
      setMarketSignal(getBuySellSignal(statsAllStrikeData));
    }
    nseWebsiteOIResponseData?.preOpenStatusModel && setPreopenMarketData(nseWebsiteOIResponseData.preOpenStatusModel);
    nseWebsiteOIResponseData?.liveAnalysisCount && setLiveAnalysisMarketData(nseWebsiteOIResponseData.liveAnalysisCount);
    setNseWebsiteOIResposneDataList(buildNseWebsiteOIResponseOIDataMap(nseWebsiteOIResponseData));
    console.log('nseWebsiteOIResposneDataList => ', nseWebsiteOIResposneDataList);
  }, [options, selectedStrikeFilter, updateTopCallVolumes, nseWebsiteOIResponseData,
    statsAllStrikeData.callTotalOICHGSum,
    statsAllStrikeData.putTotalOICHGSum,
    statsAllStrikeData.callTotalOISum,
    statsAllStrikeData.putTotalOISum,
    statsAllStrikeData.callTotalVolumeSum,
    statsAllStrikeData.putTotalVolumeSum,
  ]);

  const buildNseWebsiteOIResponseOIDataMap = (response) => {
    const getOIData = (key) => {
      const responseData = response?.data || [];

      for (let item of responseData) {
        const source = item?.categories || item; // supports both cases
        const value = source?.[key];
        if (value) return value;
      }
      return [];
    };

    return {
      "Rise-in-OI-Rise": getOIData("Rise-in-OI-Rise"),
      "Rise-in-OI-Slide": getOIData("Rise-in-OI-Slide"),
      "Slide-in-OI-Rise": getOIData("Slide-in-OI-Rise"),
      "Slide-in-OI-Slide": getOIData("Slide-in-OI-Slide")
    };
  };
  // const filter5thStrikeNiftyOptionData = (data, spot) => {
  //   const range = Number(4);
  //   const closestIndex = data.reduce((closestIdx, curr, idx) =>
  //     Math.abs(curr.strikePrice - spot) < Math.abs(data[closestIdx].strikePrice - spot)
  //       ? idx : closestIdx, 0);
  //   const start = Math.max(0, closestIndex - range);
  //   const end = Math.min(data.length - 1, closestIndex + range);
  //   return data.slice(start, end + 2);
  // };
  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkSignal((prev) => !prev);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const filterByCallOiChgPercentage = (list) => {
    return list.filter((row) => {
      const percentage = parseFloat(
        getCallAllStrikeOiChgPercentage(row.ceOIChg)
      );
      return percentage > 50;
    });
  };

  const filterByPutOiChgPercentage = (list) => {
    return list.filter((row) => {
      const percentage = parseFloat(
        getPutAllStrikeOiChgPercentage(row.peOIChg)
      );
      return percentage > 50;
    });
  };

  // -------------------
  // Filter Option Data
  // -------------------

  const calculateCEPEValues = (fifthStrikeDataCalculation, spotPrice) => {
    let ceOISum = 0;
    let ceOICHGSum = 0;
    let ceVolumeSum = 0;

    let peOISum = 0;
    let peOICHGSum = 0;
    let peVolumeSum = 0;

    // ✅ Guard check for array
    if (!Array.isArray(fifthStrikeDataCalculation)) {
      return {
        ceValueSum: { ceOISum, ceOICHGSum, ceVolumeSum },
        peValueSum: { peOISum, peOICHGSum, peVolumeSum },
      };
    }

    fifthStrikeDataCalculation.forEach((item) => {
      // ✅ Skip null/undefined items
      if (!item) return;

      const strike = Number(item?.strikePrice);

      // ✅ Skip invalid strike
      if (isNaN(strike)) return;

      if (strike > spotPrice) {
        ceOISum += Number(item?.ceOI ?? 0);
        ceOICHGSum += Number(item?.ceOIChg ?? 0);
        ceVolumeSum += Number(item?.ceVolume ?? 0);
      } else if (strike < spotPrice) {
        peOISum += Number(item?.peOI ?? 0);
        peOICHGSum += Number(item?.peOIChg ?? 0);
        peVolumeSum += Number(item?.peVolume ?? 0);
      }
    });

    return {
      ceValueSum: { ceOISum, ceOICHGSum, ceVolumeSum },
      peValueSum: { peOISum, peOICHGSum, peVolumeSum },
    };
  };

  const filterNiftyOptionData = (data, spot, selectedStrike) => {
    if (!spot || selectedStrike === "ALL") return data;

    if (!spot || selectedStrike === "10") {
      return strikeFilterData(data, spot, selectedStrike)
    }
    if (!spot || selectedStrike === "5") {
      return strikeFilterData(data, spot, selectedStrike)
    }
  };

  const strikeFilterData = (data, spot, strikeCount) => {
    const range = Number(strikeCount - 1);
    const closestIndex = data.reduce((closestIdx, curr, idx) =>
      Math.abs(curr.strikePrice - spot) < Math.abs(data[closestIdx].strikePrice - spot)
        ? idx : closestIdx, 0);
    const start = Math.max(0, closestIndex - range);
    const end = Math.min(data.length - 1, closestIndex + range);
    return data.slice(start, end + 2);
  }
  // const filterNiftyOptionData = (data, spot) => {
  //   if (!spot || selectedStrikeFilter === "ALL") return data;
  //   const range = Number(selectedStrikeFilter - 1);
  //   const closestIndex = data.reduce((closestIdx, curr, idx) =>
  //     Math.abs(curr.strikePrice - spot) < Math.abs(data[closestIdx].strikePrice - spot)
  //       ? idx : closestIdx, 0);
  //   const start = Math.max(0, closestIndex - range);
  //   const end = Math.min(data.length - 1, closestIndex + range);
  //   return data.slice(start - 1, end + 1);
  // };

  const handleIndexFilterValueChange = (event) => {
    setIndexFilterValueChangeFilter(event.target.value);
  };
  // Percentage Helper Functions
  // =======================
  const getCallVolumePercentage = (volume) =>
    stats.highestCallVolume && volume ? ((volume / stats.highestCallVolume) * 100).toFixed(2) + "%" : "0%";

  const getPutVolumePercentage = (volume) => stats.highestPutVolume && volume ? ((volume / stats.highestPutVolume) * 100).toFixed(2) + "%"
    : "0%";

  const getCallOiPercentage = (oi) => stats.highestCallOi && oi ? ((oi / stats.highestCallOi) * 100).toFixed(2) + "%"
    : "0%";
  const getCallTotalVolumePercentage = (val) => statsAllStrikeData.highestCallTotalVolume && val ? ((val / statsAllStrikeData.highestCallTotalVolume) * 100).toFixed(2) + '%' : '0%';

  const getPutAllStrikeOiPercentage = (oi) => statsAllStrikeData.highestPutAllOI && oi ? ((oi / statsAllStrikeData.highestPutAllOI) * 100).toFixed(2) + "%"
    : "0%";

  const getPutOiPercentage = (oi) => stats.highestPutOi && oi ? ((oi / stats.highestPutOi) * 100).toFixed(2) + "%"
    : "0%";

  const getCallAllStrikeOiPercentage = (oi) => statsAllStrikeData.highestCallAllOI && oi ? ((oi / statsAllStrikeData.highestCallAllOI) * 100).toFixed(2) + "%"
    : "0%";
  const getCallOiChgPercentage = (val) => stats.highestCallOiChg && val ? ((val / stats.highestCallOiChg) * 100).toFixed(2) + "%"
    : "0%";

  const getCallAllStrikeOiChgPercentage = (val) => statsAllStrikeData.highestCallAllOIChg && val ? ((val / statsAllStrikeData.highestCallAllOIChg) * 100).toFixed(2) + "%"
    : "0%";
  const getPutOiChgPercentage = (val) => stats.highestPutOiChg && val ? ((val / stats.highestPutOiChg) * 100).toFixed(2) + "%"
    : "0%";

  const getPutAllStrikeOiChgPercentage = (val) => statsAllStrikeData.highestPutAllOIChg && val ? ((val / statsAllStrikeData.highestPutAllOIChg) * 100).toFixed(2) + "%"
    : "0%";

  const getPutTotalVolumePercentage = (val) => statsAllStrikeData.highestPutTotalVolume && val
    ? ((val / statsAllStrikeData.highestPutTotalVolume) * 100).toFixed(2) + '%' : '0%';

  // === Call OI Change Classes ===
  const getCallOiClass = (oi) => {
    let highestOI = stats.highestCallOi;
    let secondHighestOI = stats.secondHighestCallOi;
    if (!highestOI || !oi) return '';
    const pct = (oi / highestOI) * 100;
    if (oi === highestOI) return 'call-oi-blue';
    if (oi === secondHighestOI && pct >= 75) return 'call-oi-yellow';
    if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'call-oi-bold';
    return '';
  };

  const getCallAllStrikeOiClass = (oi) => {
    let highestOI = statsAllStrikeData.highestCallAllOI;
    let secondHighestOI = statsAllStrikeData.secondHighestCallAllOI;
    if (!highestOI || !oi) return '';
    const pct = (oi / highestOI) * 100;
    if (oi === highestOI) return 'call-oi-blue';
    if (oi === secondHighestOI && pct >= 75) return 'call-oi-yellow';
    if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'call-oi-bold';
    return '';
  };

  const getCallOiChgClass = (val) => {
    let highestOIChg = stats.highestCallOiChg;
    let secondHighestOIChg = stats.secondHighestCallOiChg;
    if (!highestOIChg || !val) return '';
    const pct = (val / highestOIChg) * 100;
    if (val === highestOIChg) return 'call-oiChg-blue';
    if (val === secondHighestOIChg && pct >= 75) return 'call-oiChg-yellow';
    if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'call-oiChg-bold';
    return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
  };

  const getCallAllStrikeOiChgClass = (val) => {
    let highestOIChg = statsAllStrikeData.highestCallAllOIChg;
    let secondHighestOIChg = statsAllStrikeData.secondHighestCallAllOIChg;
    if (!highestOIChg || !val) return '';
    const pct = (val / highestOIChg) * 100;
    if (val === highestOIChg) return 'call-oiChg-blue';
    if (val === secondHighestOIChg && pct >= 75) return 'call-oiChg-yellow';
    if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'call-oiChg-bold';
    return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
  };
  // === Put OI Change Classes ===
  const getPutOiClass = (oi) => {
    let highestOI = stats.highestPutOi;
    let secondHighestOI = stats.secondHighestPutOi;
    if (!highestOI || !oi) return '';
    const pct = (oi / highestOI) * 100;
    if (oi === highestOI) return 'put-oi-blue';
    if (oi === secondHighestOI && pct >= 75) return 'put-oi-yellow';
    if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'put-oi-bold';
    return '';
  };

  const getPutAllStrikeOiClass = (oi) => {
    let highestOI = statsAllStrikeData.highestPutAllOI;
    let secondHighestOI = statsAllStrikeData.secondHighestPutAllOI;
    if (!highestOI || !oi) return '';
    const pct = (oi / highestOI) * 100;
    if (oi === highestOI) return 'put-oi-blue';
    if (oi === secondHighestOI && pct >= 75) return 'put-oi-yellow';
    if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'put-oi-bold';
    return '';
  };

  const getPutOiChgClass = (val) => {
    let highestOIChg = stats.highestPutOiChg;
    let secondHighestOIChg = stats.secondHighestPutOiChg;
    if (!highestOIChg || !val) return '';
    const pct = (val / highestOIChg) * 100;
    if (val === highestOIChg) return 'put-oiChg-blue';
    if (val === secondHighestOIChg && pct >= 75) return 'put-oiChg-yellow';
    if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'put-oiChg-bold';
    return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
  };

  const getPutAllStrikeOiChgClass = (val) => {
    let highestOIChg = statsAllStrikeData.highestPutAllOIChg;
    let secondHighestOIChg = statsAllStrikeData.secondHighestPutAllOIChg;
    if (!highestOIChg || !val) return '';
    const pct = (val / highestOIChg) * 100;
    if (val === highestOIChg) return 'put-oiChg-blue';
    if (val === secondHighestOIChg && pct >= 75) return 'put-oiChg-yellow';
    if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'put-oiChg-bold';
    return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
  };
  const getCallTotalVolumeClass = (callTotalVolume) => {
    let highestVolume = statsAllStrikeData.highestCallTotalVolume;
    let secondHighestVolume = statsAllStrikeData.secondHighestCallTotalVolume;
    if (!highestVolume || !callTotalVolume) return '';
    const pct = (callTotalVolume / highestVolume) * 100;
    if (callTotalVolume === highestVolume) return 'call-oi-blue';
    if (callTotalVolume === secondHighestVolume && pct >= 75) return 'call-oi-yellow';
    if (pct >= 75 && callTotalVolume < (secondHighestVolume ?? Infinity)) return 'call-oi-bold';
    return '';
  };

  const getPutTotalVolumeClass = (putTotalVolume) => {
    let highestVolume = statsAllStrikeData.highestPutTotalVolume;
    let secondHighestVolume = statsAllStrikeData.secondHighestPutTotalVolume;
    if (!highestVolume || !putTotalVolume) return '';
    const pct = (putTotalVolume / highestVolume) * 100;
    if (putTotalVolume === highestVolume) return 'call-oi-blue';
    if (putTotalVolume === secondHighestVolume && pct >= 75) return 'call-oi-yellow';
    if (pct >= 75 && putTotalVolume < (secondHighestVolume ?? Infinity)) return 'call-oi-bold';
    return '';
  };

  const formatIndianNumber = (value) =>
    value != null && !isNaN(value) ? Math.round(Number(value)).toLocaleString("en-IN") : "--";

  // =======================
  // BULLISH / BEARISH HEADER SIGNAL
  // =======================
  const getBullBearHeaderSignal = (statsAllStrikeData) => {
    if (!statsAllStrikeData) return { text: "NO SIGNAL", className: "neutral" };

    const { callAllStrikeTotalOISum, putAllStrikeTotalOISum, callAllStrikeTotalOICHGSum,
      putAllStrikeTotalOICHGSum, callAllStrikeTotalVolumeSum, putAllStrikeTotalVolumeSum,
    } = statsAllStrikeData;

    // 🟢 BULLISH
    if ((callAllStrikeTotalOISum > putAllStrikeTotalOISum &&
      callAllStrikeTotalOICHGSum < putAllStrikeTotalOICHGSum && callAllStrikeTotalVolumeSum > putAllStrikeTotalVolumeSum)) {
      return {
        text: `BULLISH → EOR/EOR + 1`,
        className: `header-signal buy-deep ${blinkSignal ? "blink" : ""}`
      };
    }

    // 🔴 BEARISH
    if ((callAllStrikeTotalOISum < putAllStrikeTotalOISum &&
      callAllStrikeTotalOICHGSum > putAllStrikeTotalOICHGSum && callAllStrikeTotalVolumeSum < putAllStrikeTotalVolumeSum)) {
      return {
        text: `BEARISH → EOS/EOS-1`,
        className: `header-signal sell-rise ${blinkSignal ? "blink" : ""}`
      };
    }

    return { text: "NO SIGNAL", className: "neutral" };
  };

  const getBullBearSignal = (statsAllStrikeData) => {
    if (!statsAllStrikeData) return { text: "NO SIGNAL", className: "neutral" };

    const { callAllStrikeTotalOISum, putAllStrikeTotalOISum, callAllStrikeTotalOICHGSum,
      putAllStrikeTotalOICHGSum, callAllStrikeTotalVolumeSum, putAllStrikeTotalVolumeSum,
    } = statsAllStrikeData;

    // 🟢 BULLISH
    if ((putAllStrikeTotalOISum > callAllStrikeTotalOISum && putAllStrikeTotalOICHGSum > callAllStrikeTotalOICHGSum)
      || ((putAllStrikeTotalVolumeSum < callAllStrikeTotalVolumeSum && callAllStrikeTotalOISum < putAllStrikeTotalOISum))
      || (callAllStrikeTotalOICHGSum < 0)
    ) {
      return {
        text: `BULLISH`,
        className: `header-signal buy-deep ${blinkSignal ? "blink" : ""}`
      };
    }

    // 🔴 BEARISH
    if ((putAllStrikeTotalOISum < callAllStrikeTotalOISum && putAllStrikeTotalOICHGSum < callAllStrikeTotalOICHGSum)
      || ((putAllStrikeTotalVolumeSum > callAllStrikeTotalVolumeSum && callAllStrikeTotalOISum > putAllStrikeTotalOISum))
      || (putAllStrikeTotalOICHGSum < 0)
    ) {
      return {
        text: `BEARISH`,
        className: `header-signal sell-rise ${blinkSignal ? "blink" : ""}`
      };
    }

    return { text: "NO SIGNAL", className: "neutral" };
  };

  const getVolumeBullBearSignal = (calculate5thStrikeNCEPEVAlues) => {
    if (!calculate5thStrikeNCEPEVAlues) return { text: "NO SIGNAL", className: "neutral" };
    //  ceValueSum: { ceOISum, ceOICHGSum, ceVolumeSum },
    //       peValueSum: { peOISum, peOICHGSum, peVolumeSum },
    const { ceValueSum, peValueSum,
    } = calculate5thStrikeNCEPEVAlues;

    // 🟢 BULLISH
    if ((ceValueSum.ceVolumeSum > peValueSum.peVolumeSum)) {
      return {
        text: `BULLISH`,
        className: `header-signal buy-deep ${blinkSignal ? "blink" : ""}`
      };
    }

    // 🔴 BEARISH
    if (ceValueSum.ceVolumeSum < peValueSum.peVolumeSum) {
      return {
        text: `BEARISH`,
        className: `header-signal sell-rise ${blinkSignal ? "blink" : ""}`
      };
    }

    return { text: "NO SIGNAL", className: "neutral" };
  };

  const getOIBullBearSignal = (calculate5thStrikeNCEPEVAlues) => {
    if (!calculate5thStrikeNCEPEVAlues) return { text: "NO SIGNAL", className: "neutral" };
    //  ceValueSum: { ceOISum, ceOICHGSum, ceVolumeSum },
    //       peValueSum: { peOISum, peOICHGSum, peVolumeSum },
    const { ceValueSum, peValueSum,
    } = calculate5thStrikeNCEPEVAlues;

    // 🟢 BULLISH
    if ((ceValueSum.ceOISum < peValueSum.peOISum)) {
      return {
        text: `BULLISH`,
        className: `header-signal buy-deep ${blinkSignal ? "blink" : ""}`
      };
    }

    // 🔴 BEARISH
    if (ceValueSum.ceOISum > peValueSum.peOISum) {
      return {
        text: `BEARISH`,
        className: `header-signal sell-rise ${blinkSignal ? "blink" : ""}`
      };
    }

    return { text: "NO SIGNAL", className: "neutral" };
  };

  const getOICHGBullBearSignal = (calculate5thStrikeNCEPEVAlues) => {
    if (!calculate5thStrikeNCEPEVAlues) return { text: "NO SIGNAL", className: "neutral" };
    //  ceValueSum: { ceOISum, ceOICHGSum, ceVolumeSum },
    //       peValueSum: { peOISum, peOICHGSum, peVolumeSum },
    const { ceValueSum, peValueSum,
    } = calculate5thStrikeNCEPEVAlues;

    // 🟢 BULLISH
    if ((ceValueSum.ceOICHGSum < peValueSum.peOICHGSum)) {
      return {
        text: `BULLISH`,
        className: `header-signal buy-deep ${blinkSignal ? "blink" : ""}`
      };
    }

    // 🔴 BEARISH
    if (ceValueSum.ceOICHGSum > peValueSum.peOICHGSum) {
      return {
        text: `BEARISH`,
        className: `header-signal sell-rise ${blinkSignal ? "blink" : ""}`
      };
    }

    return { text: "NO SIGNAL", className: "neutral" };
  };

  const bullBearSignal = getBullBearHeaderSignal(statsAllStrikeData);

  const bullBear = getBullBearSignal(statsAllStrikeData);
  const bullBearVolume = getVolumeBullBearSignal(calculate5thStrikeNCEPEVAlues);
  const bullBearOI = getOIBullBearSignal(calculate5thStrikeNCEPEVAlues);
  const bullBearOICHG = getOICHGBullBearSignal(calculate5thStrikeNCEPEVAlues);
  const indexSpotPrice = Number(indexDayRangeData?.niftySpotPrice || 0);

  const previousDayHigh = Number(indexDayRangeData?.previousDayHigh || 0);
  const previousDayLow = Number(indexDayRangeData?.previousDayLow || 0);
  const previousDayClose = Number(indexDayRangeData?.previousDayClose || 0);
  const dayHigh = Number(indexDayRangeData?.dayHigh || 0);
  const dayLow = Number(indexDayRangeData?.dayLow || 0);

  const isNearPDH = previousDayHigh && Math.abs(previousDayHigh - indexSpotPrice) <= 20;
  const isNearPDL = previousDayLow && Math.abs(previousDayLow - indexSpotPrice) <= 20;
  const isNearPDC = previousDayClose && Math.abs(previousDayClose - indexSpotPrice) <= 20;
  const isNearDH = dayHigh && Math.abs(dayHigh - indexSpotPrice) <= 20;
  const isNearDL = dayLow && Math.abs(dayLow - indexSpotPrice) <= 20;
  // -------------------
  // Header Data
  // -------------------
  const headerData = [

    {
      value1: renderBuySellSignal(),
    },
    {
      value1: (
        <span className={`header-signal ${bullBearSignal.className} }`}>
          {bullBearSignal.text}
        </span>
      ),
    },
    {
      value1: (
        <span className={`header-signal ${bullBear.className} }`}>
          {bullBear.text}
        </span>
      ),
    },
    {
      value1: (
        <span className={`header-signal ${bullBearVolume.className} }`}>
          {bullBearVolume.text}
        </span>
      ),
    },
    {
      value1: (
        <span className={`header-signal ${bullBearOI.className} }`}>
          {bullBearOI.text}
        </span>
      ),
    },
    {
      value1: (
        <span className={`header-signal ${bullBearOICHG.className} }`}>
          {bullBearOICHG.text}
        </span>
      ),
    },
    {
      label1: "RES",
      value1:
        getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice) + ' - ' +
        getCallResistanceWttAndWtb(getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice),
          stats.secondHighestCallVolStrike, stats.secondHighestCallVolume, stats.highestCallVolume), // just call function normally
      label2: "SUP",
      value2: getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice) + ' - ' +
        getPutSupportWttAndWtb(stats.secondHighestPutVolStrike, stats.secondHighestPutVolume, getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice))

    },
    {
      label1: "ExpDate",
      value1: indexDayRangeData?.expiryDate || "--",
      label2: "DateTime",
      value2: indexDayRangeData?.currentDateTime || "--",
    },
  ];

  usePageTitle(indexDayRangeData?.niftySpotPrice ?? "N/A", indexDayRangeData?.expiryDate ?? "N/A");

  // =======================
  // BUY / SELL SIGNAL METHOD
  // =======================
  const getBuySellSignal = (statsAllStrikeData) => {
    if (!statsAllStrikeData) return "NO_SIGNAL";

    const { callAllStrikeTotalOICHGSum, putAllStrikeTotalOICHGSum,
      callAllStrikeTotalOISum, putAllStrikeTotalOISum, callAllStrikeTotalVolumeSum, putAllStrikeTotalVolumeSum, } = statsAllStrikeData;

    // 🔴 SELL ON RISE
    if (callAllStrikeTotalOICHGSum > putAllStrikeTotalOICHGSum &&
      callAllStrikeTotalOISum > putAllStrikeTotalOISum && callAllStrikeTotalVolumeSum < putAllStrikeTotalVolumeSum) {
      return "SELL_ON_RISE";
    }

    // 🟢 BUY ON DEEP
    if (putAllStrikeTotalOICHGSum > callAllStrikeTotalOICHGSum &&
      putAllStrikeTotalOISum > callAllStrikeTotalOISum && putAllStrikeTotalVolumeSum < callAllStrikeTotalVolumeSum) {
      return "BUY_ON_DEEP";
    }
    return "NO_SIGNAL";
  };

  function renderBuySellSignal() {
    if ((marketSignal === "SELL_ON_RISE")) {
      if ((statsAllStrikeData.putAllStrikeTotalOICHGSum < 0)) {
        return (
          <span className={`header-signal sell-rise ${blinkSignal ? "blink" : ""}`}>
            <span className="put-seller-line1">SELL ON RISE</span>
            <span className="put-seller-line2">PUT SELLER EXIT, DON'T EXIT</span>
          </span>

        );
      }
      return (
        <span className={`header-signal sell-rise ${blinkSignal ? "blink" : ""}`}>SELL ON RISE</span>
      );
    }
    if ((marketSignal === "BUY_ON_DEEP")) {
      if ((statsAllStrikeData.callAllStrikeTotalOICHGSum < 0)) {
        return (
          <span className={`header-signal buy-deep ${blinkSignal ? "blink" : ""}`}>
            <span className="call-seller-line1">BUY ON DEEP</span>
            <span className="call-seller-line2">CALL SELLER EXIT, DON'T EXIT</span>
          </span>
        );
      }
      return (
        <span className={`header-signal buy-deep ${blinkSignal ? "blink" : ""}`}> BUY ON DEEP </span>
      );
    }

    return <span className="header-signal neutral">NO SIGNAL</span>;
  }
  const getFooterValueClass = (value) => (value < 0 ? "text-red" : "text-green");

  const isOpenEqualHigh = (open, high) => {
    return open != null && high != null && Number(open) === Number(high);
  };

  const isOpenEqualLow = (open, low) => {
    return open != null && low != null && Number(open) === Number(low);
  };

  // flat candle → do not show anything
  const showFlag = !(isOpenEqualHigh && isOpenEqualLow);

  const getVolumeStatus = (callVolume, putVolume) => {
    if (callVolume > putVolume) {
      return { text: "BULLISH", className: "bullish-status" };
    }

    if (putVolume > callVolume) {
      return { text: "BEARISH", className: "bearish-status" };
    }

    return { text: "--", className: "neutral-status" };
  };

  const isDiffEqualTen = (value1, value2) => {
    if (value1 == null || value2 === null) return false;

    return Math.abs(value1 - value2) <= 10;
  };

  const isIndexDiffEqualTwenty = (value1, value2) => {
    if (value1 == null || value2 === null) return false;

    return Math.abs(value1 - value2) <= 20;
  };


  const getColor = (value) => {
    const num = Number(value);

    if (num < 0) return "pre-open-red";
    if (num > 0) return "pre-open-green";
    return "text-white";
  };

  // -------------------
  // JSX Table Section (remains unchanged)
  // -------------------
  return (
    <div className="option-chain-analysis-page">
      <header className="header">
        <nav className="nav-container">
          <div className="left-nav header-flex">
            <div className="logo-container">
              <img
                src="/assets/images/noptionwave-logo-1.png"
                className="header-logo"
                alt="noptionwave logo"
              />
              {/* <span className="index-name">{indexSymbolName}</span> */}
              <select name="indexFilter" className="index-filter" value={indexFilterValueChanges} onChange={handleIndexFilterValueChange} style={{ cursor: "pointer" }}>
                <option value="NSE:NIFTY">NIFTY</option>
                <option value="BSE:SENSEX">SENSEX</option>
                <option value="NSE:BANKNIFTY">BANKNIFTY</option>
                {/* <option value="NSE:FINNIFTY">FINNIFTY</option>
                <option value="NSE:MIDCPNIFTY">MIDCPNIFTY</option> */}
              </select>
            </div>
            {headerData.map((item, idx) => (
              <div className="header-item" key={idx}>

                {/* ===== LINE 1 ===== */}
                {(item.label1 != null || item.value1 != null) && (
                  <span className={`header-line ${item.highlight ? "blink-highlight" : ""}`}>
                    {item.label1 != null && item.label1 !== "" && (
                      <>
                        {item.label1}
                        {item.value1 != null && " : "}
                      </>
                    )}

                    {item.value1 != null && item.value1}
                  </span>
                )}

                {/* ===== LINE 2 ===== */}
                {(item.label2 != null || item.value2 != null) && (
                  <span className={`header-line ${item.highlight2 ? "blink-highlight" : ""}`}>
                    {item.label2 != null && item.label2 !== "" && (
                      <>
                        {item.label2}
                        {item.value2 != null && " : "}
                      </>
                    )}
                    {item.value2 != null && item.value2}
                  </span>
                )}

              </div>
            ))}


          </div>
        </nav>
      </header>

      <div className="table-container">
        <table className="responsive-table top-table table-bordered text-center sticky-header">
          <thead>
            <tr>
              <th>RESISTANCE</th>
              <th>VOLUME</th>
              <th>OI</th>
              <th>OICHG</th>
              {/* <th>TOTAL VOLUME</th> */}
              <th>SPOT</th>
              <th>SUPPORT</th>
              <th>VOLUME</th>
              <th>OI</th>
              <th>OICHG</th>
              {/* <th>TOTAL VOLUME</th> */}
            </tr>
          </thead>
          <tbody>
            <>
              {/* Original row */}
              <tr>
                <td className="label-color">
                  {getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice)}
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestCallVolStrike}</span>
                  </span>
                  <br />
                  <span>
                    2nd Highest -
                    <span className="label-color">{stats.secondHighestCallVolStrike} -{" "} {getCallVolumePercentage(stats.secondHighestCallVolume)}
                    </span>
                  </span>
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestCallOiStrike}</span>
                  </span>
                  <br />
                  <span>
                    2nd Highest -
                    <span className="label-color">{stats.secondHighestCallOiStrike} -{" "} {getCallOiPercentage(stats.secondHighestCallOi)} </span>
                  </span>
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestCallOiChgStrike}</span>
                  </span><br />
                  <span>
                    2nd Highest - <span className="label-color">{stats.secondHighestCallOiChgStrike} -{" "} {getCallOiChgPercentage(stats.secondHighestCallOiChg)}</span>
                  </span>
                </td>

                <td className="spot-price-color">
                  {stats.spotPrice}
                </td>

                <td className="label-color">
                  {getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice)}
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestPutVolStrike}</span>
                  </span>
                  <br />
                  <span>
                    2nd Highest -
                    <span className="label-color">{stats.secondHighestPutVolStrike} -{" "}
                      {getPutVolumePercentage(stats.secondHighestPutVolume)} </span>
                  </span>
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestPutOiStrike}</span>
                  </span>
                  <br />
                  <span>
                    2nd Highest - <span className="label-color">{stats.secondHighestPutOiStrike} -{" "}
                      {getPutOiPercentage(stats.secondHighestPutOi)}</span>
                  </span>
                </td>

                <td>
                  <span>1st Highest -
                    <span className="label-color">{stats.highestPutOiChgStrike}</span>
                  </span>
                  <br />
                  <span>
                    2nd Highest -
                    <span className="label-color">{stats.secondHighestPutOiChgStrike} -{" "}
                      {getPutOiChgPercentage(stats.secondHighestPutOiChg)}</span>
                  </span>
                </td>
              </tr>

              <tr >
                <td colSpan="2" className="label-color" >
                  Pre Open :
                  <span className="span-text-font">
                    {formatIndianNumber(preopenMarketData?.lastPrice)}
                  </span> |
                  <span className={`span-text-font ${getColor(preopenMarketData?.change)}`} >
                    PTS {formatIndianNumber(preopenMarketData?.change)}
                  </span> |
                  <span className={`span-text-font ${getColor(preopenMarketData?.pChange)}`} >
                    CHG(%) {preopenMarketData?.pChange}
                  </span>
                </td>
                <td colSpan="1" className="label-color ">
                  <span className="text-green"> Advance : </span>
                  <span className="text-green span-text-font">{formatIndianNumber(preopenMarketData?.advances)}</span>
                </td>

                <td colSpan="1" className="label-color">
                  <span className="text-red"> Decline :</span>
                  <span className="text-red span-text-font">{formatIndianNumber(preopenMarketData?.declines)}</span>
                </td>

                <td colSpan="1" className="label-color">
                  <span className="label-color"> Unchanged :</span>
                  <span className="span-text-font">{formatIndianNumber(preopenMarketData?.unchanged)}</span>
                </td>
                <td colSpan="1" className="label-color">
                  Live Analysis
                </td>
                <td colSpan="1" className="label-color">
                  <span className="text-green"> Advance :</span>
                  <span className="text-green span-text-font">{formatIndianNumber(liveAnalysisMarketData?.Advances)}</span>
                </td>

                <td colSpan="1" className="label-color">
                  <span className="text-red"> Decline :</span>
                  <span className="text-red span-text-font">{formatIndianNumber(liveAnalysisMarketData?.Declines)}</span>
                </td>

                <td colSpan="1" >
                  <span className="label-color"> Unchanged :</span>
                  <span className="span-text-font">{formatIndianNumber(liveAnalysisMarketData?.Unchange)}</span>

                </td>
              </tr>
              {/* Fixed Footer start Rows for PDH PDL PDC and OPEN price  */}
              <tr >
                <td colSpan="2" className="label-color">
                  <span className={`label-line ${isNearPDH ? "highlight-cell blink-highlight" : ""}`} >PDH :
                    {indexDayRangeData?.previousDayHigh ? Number(indexDayRangeData.previousDayHigh).toFixed(2) : "--"}
                  </span>
                </td>

                <td colSpan="1" className="label-color">
                  <span className={`label-line ${isNearPDL ? "highlight-cell blink-highlight" : ""}`} >PDL :
                    {indexDayRangeData?.previousDayLow !== undefined && indexDayRangeData?.previousDayLow !== null ? Number(indexDayRangeData.previousDayLow).toFixed(2) : "--"}
                  </span>
                </td>

                <td colSpan="1" className="label-color">
                  <span className={`label-line ${isNearPDC ? "highlight-cell blink-highlight" : ""}`} > PDC :
                    {indexDayRangeData?.previousDayClose !== undefined && indexDayRangeData?.previousDayClose !== null ? Number(indexDayRangeData.previousDayClose).toFixed(2) : "--"}
                  </span>
                </td>

                {/* <td ç>
                  <span className={getFooterValueClass(statsAllStrikeData.putAllStrikeTotalOICHGSum - statsAllStrikeData.callAllStrikeTotalOICHGSum)} > PCR 
                     ( {formatIndianNumber(statsAllStrikeData.putAllStrikeTotalOICHGSum - statsAllStrikeData.callAllStrikeTotalOICHGSum)}  )
                    =
                    {Number(  statsAllStrikeData.putAllStrikeTotalOICHGSum / statsAllStrikeData.callAllStrikeTotalOICHGSum ).toFixed(2)}
                  </span>
                </td> */}
                <td colSpan="1" className="spot-price-color">
                  {/* SPOT - {options?.[indexFilterValueChanges]?.indexLiveDataModel?.lp} */}
                  <span className={isIndexDiffEqualTwenty(indexDayRangeData?.indexPivotPoint, indexDayRangeData?.niftySpotPrice) ? "index-blink-mid-point" : ""}>
                    MID - {indexDayRangeData ? Number(indexDayRangeData?.indexPivotPoint).toFixed(2) : "--"}
                  </span>
                </td>

                <td colSpan="1" className="label-color">
                  OPEN : {indexDayRangeData ? Number(indexDayRangeData.niftyOpenPrice || 0).toFixed(2) : "--"}
                </td>

                <td colSpan="1" className="label-color">
                  <span className={`label-line ${isNearDH ? "highlight-cell blink-highlight" : ""}`} >
                    DH : {indexDayRangeData?.dayHigh !== undefined && indexDayRangeData?.dayHigh !== null ? Number(indexDayRangeData.dayHigh).toFixed(2) : "--"}
                  </span>
                </td>

                <td colSpan="2" className="label-color">
                  <span className={`label-line ${isNearDL ? "highlight-cell blink-highlight" : ""}`} >
                    DL : {indexDayRangeData?.dayLow !== undefined && indexDayRangeData?.dayLow !== null ? Number(indexDayRangeData.dayLow).toFixed(2) : "--"}
                  </span>
                </td>
              </tr>


              {/* Fixed Footer end Rows for PDH PDL PDC and OPEN price  */}

              <tr className="label-color">
                <td colSpan="2">
                  Total-OICHG :  ({formatIndianNumber(statsAllStrikeData.callAllStrikeTotalOICHGSum)})
                </td>

                <td colSpan="1">
                  Total-OI : ({formatIndianNumber(statsAllStrikeData.callAllStrikeTotalOISum)})
                </td>

                <td colSpan="1">
                  Total-VOL : ({formatIndianNumber(statsAllStrikeData.callAllStrikeTotalVolumeSum)})
                </td>

                <td colSpan="1">
                  <span className={getFooterValueClass(statsAllStrikeData.putAllStrikeTotalOICHGSum - statsAllStrikeData.callAllStrikeTotalOICHGSum)} >
                    PCR
                    ( {formatIndianNumber(statsAllStrikeData.putAllStrikeTotalOICHGSum - statsAllStrikeData.callAllStrikeTotalOICHGSum)} )
                    = {Number(statsAllStrikeData.putAllStrikeTotalOICHGSum / statsAllStrikeData.callAllStrikeTotalOICHGSum).toFixed(2)}
                  </span>
                </td>

                <td colSpan="1">
                  Total-VOL :  ({formatIndianNumber(statsAllStrikeData.putAllStrikeTotalVolumeSum)})
                </td>

                <td colSpan="1">
                  Total-OI : ({formatIndianNumber(statsAllStrikeData.putAllStrikeTotalOISum)})
                </td>

                <td colSpan="2">
                  Total-OICHG : ({formatIndianNumber(statsAllStrikeData.putAllStrikeTotalOICHGSum)})
                </td>
              </tr>
              {/* live analysis count data and preopen data */}

            </>
          </tbody>
        </table>
      </div>


      <div className="option-chain-wrapper">
        <div className="ce-container">
          <table className="responsive-table responsive-table-ce table-bordered text-center sticky-header">
            <thead>
              <tr>
                <th>SYMBOL</th>
                <th>INSTRUMENT</th>
                <th>EXPIRY</th>
                <th>STRIKE PRICE</th>
                <th>OPTION TYPE</th>
                <th>LTP</th>
                <th>OICHG</th>
              </tr>
            </thead>
            <tbody>
              {nseWebsiteOIResposneDataList?.["Rise-in-OI-Rise"]
                ?.filter(row => row.instrument?.toLowerCase() === "index options")
                .map((row, index) => (
                  <tr key={index}>
                    <td>{row.symbol ?? "--"}</td>
                    <td> {row.instrument ?? "--"} </td>
                    <td> {row.expiryDate ?? "--"} </td>
                    <td className="hedge-strike-price-color">{row.strikePrice}</td>
                    <td >
                      <span className={
                        row.optionType?.toLowerCase() === "call" ? "call-row" : row.optionType?.toLowerCase() === "put" ? "put-row" : ""}>
                        {row.optionType?.toUpperCase() ?? "--"}
                      </span>
                    </td>
                    <td> {row.ltp ?? "--"} </td>
                    <td> {row.changeInOI ?? "--"} </td>
                  </tr>
                ))}
            </tbody>
            <tfoot className="fixed-footer">
              <tr className="footer-left">
                <td colSpan="15">noptionwave © 2026</td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>

      {/* <div className="volume-table-container">
        <table className="responsive-table responsive-table-volume table-bordered text-center sticky-header">
          <thead>
            <tr>
              <th>RANGE</th>
              <th> LTP</th>
              <th >REVERSAL</th>
              <th className="spot-price-color">
                SPOT - {options?.[indexFilterValueChanges]?.indexLiveDataModel?.lp}
              </th>
              <th >REVERSAL</th>
              <th> LTP</th>
              <th>RANGE</th>
            </tr>
          </thead>
          <tbody>
            {filter5thStrikeNiftyOptionDataList?.length > 0 ? (
              filter5thStrikeNiftyOptionDataList.map((row, idx) => {
                const nextRow = filter5thStrikeNiftyOptionDataList[idx + 1];

                const spot =
                  options?.[indexFilterValueChanges]?.indexLiveDataModel?.lp;

                const isSpotBetween =
                  nextRow &&
                  spot != null &&
                  spot > Math.min(row.strikePrice, nextRow.strikePrice) &&
                  spot < Math.max(row.strikePrice, nextRow.strikePrice);

                return (
                  <React.Fragment key={idx}>



                    <tr>
                      <td>
                        <span className={isDiffEqualTen(row.callPivotPointResistanceOne, row.ceLtp) ? "index-blink-resistance" : ""}>
                          R1-{row.callPivotPointResistanceOne ? Number(row.callPivotPointResistanceOne).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.callPivotPointResistanceTwo, row.ceLtp) ? "index-blink-resistance" : ""}>
                          R2-{row.callPivotPointResistanceTwo ? Number(row.callPivotPointResistanceTwo).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.callPivotPointResistanceThree, row.ceLtp) ? "index-blink-resistance" : ""}>
                          R3-{row.callPivotPointResistanceThree ? Number(row.callPivotPointResistanceThree).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.callPivotPoint, row.ceLtp) ? "index-blink-mid-point" : ""}>
                          Mid-{row.callPivotPoint ? Number(row.callPivotPoint).toFixed(2) : "--"}
                        </span>

                        <br />

                        <span className={isDiffEqualTen(row.callPivotPointSupportOne, row.ceLtp) ? "index-blink-support" : ""}>
                          S1-{row.callPivotPointSupportOne ? Number(row.callPivotPointSupportOne).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.callPivotPointSupportTwo, row.ceLtp) ? "index-blink-support" : ""}>
                          S2-{row.callPivotPointSupportTwo ? Number(row.callPivotPointSupportTwo).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.callPivotPointSupportThree, row.ceLtp) ? "index-blink-support" : ""}>
                          S3-{row.callPivotPointSupportThree ? Number(row.callPivotPointSupportThree).toFixed(2) : "--"}
                        </span>
                      </td>

                      <td>
                        {row.ceLtp != null ? Number(row.ceLtp).toFixed(2) : "--"}
                      </td>

                      <td>{Number(row.ceReversal).toFixed(2) ?? "--"}</td>

                      <td className="hedge-strike-price-color">
                        {row.strikePrice}
                      </td>

                      <td>{Number(row.peReversal).toFixed(2) ?? "--"}</td>

                      <td>
                        {row.peLtp != null ? Number(row.peLtp).toFixed(2) : "--"}
                      </td>

                      <td>
                        <span className={isDiffEqualTen(row.putPivotPointResistanceOne, row.peLtp) ? "index-blink-resistance" : ""}>
                          R1-{row.putPivotPointResistanceOne ? Number(row.putPivotPointResistanceOne).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.putPivotPointResistanceTwo, row.peLtp) ? "index-blink-resistance" : ""}>
                          R2-{row.putPivotPointResistanceTwo ? Number(row.putPivotPointResistanceTwo).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.putPivotPointResistanceThree, row.peLtp) ? "index-blink-resistance" : ""}>
                          R3-{row.putPivotPointResistanceThree ? Number(row.putPivotPointResistanceThree).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.putPivotPoint, row.peLtp) ? "index-blink-mid-point" : ""}>
                          Mid-{row.putPivotPoint ? Number(row.putPivotPoint).toFixed(2) : "--"}
                        </span>

                        <br />

                        <span className={isDiffEqualTen(row.putPivotPointSupportOne, row.peLtp) ? "index-blink-support" : ""}>
                          S1-{row.putPivotPointSupportOne ? Number(row.putPivotPointSupportOne).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.putPivotPointSupportTwo, row.peLtp) ? "index-blink-support" : ""}>
                          S2-{row.putPivotPointSupportTwo ? Number(row.putPivotPointSupportTwo).toFixed(2) : "--"}
                        </span>

                        <span className={isDiffEqualTen(row.putPivotPointSupportThree, row.peLtp) ? "index-blink-support" : ""}>
                          S3-{row.putPivotPointSupportThree ? Number(row.putPivotPointSupportThree).toFixed(2) : "--"}
                        </span>
                      </td>

                    </tr>
                    {isSpotBetween && (
                      <tr className="spot-line">
                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotResistanceThree, indexDayRangeData.niftySpotPrice) ? "index-blink-resistance" : ""}>
                            R3 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotResistanceThree).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotResistanceTwo, indexDayRangeData.niftySpotPrice) ? "index-blink-resistance" : ""}>
                            R2 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotResistanceTwo).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotResistanceOne, indexDayRangeData.niftySpotPrice) ? "index-blink-resistance" : ""}>
                            R1 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotResistanceOne).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotPoint, indexDayRangeData.niftySpotPrice) ? "index-blink-mid-point" : ""}>
                            MID - {indexDayRangeData ? Number(indexDayRangeData.indexPivotPoint).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotSupportOne, indexDayRangeData.niftySpotPrice) ? "index-blink-support" : ""}>
                            S1 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotSupportOne).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotSupportTwo, indexDayRangeData.niftySpotPrice) ? "index-blink-support" : ""}>
                            S2 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotSupportTwo).toFixed(2) : "--"}
                          </span>
                        </td>

                        <td colSpan="1">
                          <span className={isIndexDiffEqualTwenty(indexDayRangeData.indexPivotSupportThree, indexDayRangeData.niftySpotPrice) ? "index-blink-support" : ""}>
                            S3 - {indexDayRangeData ? Number(indexDayRangeData.indexPivotSupportThree).toFixed(2) : "--"}
                          </span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="7">Waiting for live data...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div> */}
    </div>

  );
}
